import { Capacitor } from "@capacitor/core";
import type { Auth } from "firebase/auth";
import type { AccountLinkKind, AccountLinkProvider, LinkRequest } from "@yjsgames/account-link";
import { deriveAccountStatus } from "@yjsgames/account-link";
import { mapLinkError } from "@yjsgames/account-link";

export interface AccountLinkConfig {
  /** authStateReady를 끝낸 Firebase Auth — currentUser(익명)에 연동한다. */
  readonly auth: Auth;
}

/**
 * 계정 연동 Provider 결정 (DIP 플랫폼 분기 팩토리).
 * - Android: `PlayGamesAccountLinkProvider`(linkWithPlayGames).
 * - iOS: `GameCenterAccountLinkProvider`(linkWithGameCenter).
 * - 웹: **null** — GPGS/Game Center가 없으므로 연동 미지원(설정 모달이 버튼을 숨긴다).
 *
 * 네이티브 어댑터는 `void import()`로 로드(웹 번들에서 네이티브 SDK 제외 + 첫 연동 전 교체 완료).
 * 반환값은 **안정 facade** — getStatus는 즉시(currentUser.providerData 파생) 동작하고, link는 어댑터
 * 로드 완료 후 위임한다. 미로드 상태에서 link 호출 시 generic 에러(부팅 시 로드 완료라 실상 미발생).
 */
export function createAccountLinkProvider(config: AccountLinkConfig): AccountLinkProvider | null {
  const platform = Capacitor.getPlatform();
  if (platform === "android") {
    return nativeFacade(config.auth, "playGames", () =>
      import("./PlayGamesAccountLinkProvider.js").then(
        ({ PlayGamesAccountLinkProvider }) => new PlayGamesAccountLinkProvider(config.auth),
      ),
    );
  }
  if (platform === "ios") {
    return nativeFacade(config.auth, "gameCenter", () =>
      import("./GameCenterAccountLinkProvider.js").then(
        ({ GameCenterAccountLinkProvider }) => new GameCenterAccountLinkProvider(config.auth),
      ),
    );
  }
  return null; // 웹 — 연동 UI 없음
}

/** 네이티브 어댑터 facade — getStatus 즉시 동작 + 어댑터 로드 후 link 위임. */
function nativeFacade(
  auth: Auth,
  kind: AccountLinkKind,
  loader: () => Promise<AccountLinkProvider>,
): AccountLinkProvider {
  let active: AccountLinkProvider | null = null;
  void loader().then((p) => {
    active = p;
  });
  return {
    kind,
    isSupported: () => true,
    getStatus: () => deriveAccountStatus(auth.currentUser?.providerData ?? []),
    link: (req: LinkRequest) => {
      if (active) active.link(req);
      else req.onError?.(mapLinkError("unknown"));
    },
  };
}
